import { Box, Typography } from "@mui/material";
import type { ReactNode } from "react";

/** Tone → value colour token. Mirrors the prior emerald/amber/red set. */
const TONE_COLOR: Record<"neutral" | "good" | "warn" | "bad", string> = {
  neutral: "text.primary",
  good: "success.main",
  warn: "warning.main",
  bad: "error.main",
};

/**
 * Single KPI card used on the Reports page. Big value, small label, an
 * optional sub-line for direction-of-travel ("+12% vs last 30d"). Kept
 * dependency-free so it composes well inside grids without dragging
 * recharts in for a number that wants no chart.
 */
export function KpiCard({
  label,
  value,
  hint,
  tone = "neutral",
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: "neutral" | "good" | "warn" | "bad";
}): JSX.Element {
  return (
    <Box
      sx={{
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        boxShadow: (t) => t.shadows[1],
        p: 2.5,
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 500,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          color: "text.disabled",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          mt: 1,
          fontSize: "1.875rem",
          fontWeight: 600,
          letterSpacing: "-0.02em",
          color: TONE_COLOR[tone],
        }}
      >
        {value}
      </Typography>
      {hint && (
        <Typography sx={{ mt: 0.5, fontSize: 12, color: "text.disabled" }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
}
